import { gql } from "@apollo/client";
import { ActorRefFrom } from "xstate";
import { createModel } from "xstate/lib/model";

import { graphQLClient } from "../../api/graphql";
import {
  CollectionNoteIDRangeFilter,
  openReportURL,
  SelectedSupplier,
  SupplierFilter,
} from "../generateReportConfig.machine";

export interface NotesReportActor extends ActorRefFrom<typeof notesReportMachine> {}

export const notesReportModel = createModel(
  {
    dateRange: {
      startDate: new Date(),
      endDate: new Date(),
    },
    supplierFilter: undefined as SupplierFilter | undefined,
    collectionNoteIDRangeFilter: undefined as CollectionNoteIDRangeFilter | undefined,
    includeCollectionNoteValues: false,
    includeInvoicedCollectionNotes: false,
    reportURL: undefined as string | undefined,
    error: undefined as string | undefined,
  },
  {
    events: {
      provideReportDates: (value: { startDate: Date; endDate: Date }) => ({ data: value }),
      provideSupplierFilter: (value: { from: SelectedSupplier; to: SelectedSupplier }) => ({
        data: value,
      }),
      provideCollectionNoteIDRangeFilter: (value: { fromID: number; toID: number }) => ({
        data: value,
      }),
      provideAdditionalFilterSettings: (value: {
        includeCollectionNoteValues: boolean;
        includeInvoicedCollectionNotes: boolean;
      }) => ({ data: value }),
      skipStep: () => ({}),
      goBack: () => ({}),
      generateReport: () => ({}),
      reportGenerationSuccess: (value: string) => ({ data: { reportURL: value } }),
      reportGenerationFailure: (value: string) => ({ data: { error: value } }),
      restart: () => ({}),
    },
  }
);

export const notesReportMachine = notesReportModel.createMachine(
  {
    context: notesReportModel.initialContext,
    initial: "configuringDateRange",
    states: {
      configuringDateRange: {
        on: {
          provideReportDates: {
            actions: notesReportModel.assign({
              dateRange: (_, ev) => {
                return ev.data;
              },
            }),
            target: "configuringSupplierFilter",
          },
        },
      },
      configuringSupplierFilter: {
        on: {
          provideSupplierFilter: {
            actions: notesReportModel.assign({
              supplierFilter: (_, ev) => {
                return ev.data;
              },
            }),
            target: "configuringNoteIDRange",
          },
          skipStep: {
            actions: notesReportModel.assign({
              supplierFilter: () => undefined,
            }),
            target: "configuringNoteIDRange",
          },
          goBack: {
            target: "configuringDateRange",
          },
        },
      },
      configuringNoteIDRange: {
        on: {
          provideCollectionNoteIDRangeFilter: {
            actions: notesReportModel.assign({
              collectionNoteIDRangeFilter: (_, ev) => {
                return { fromID: ev.data.fromID, toID: ev.data.toID };
              },
            }),
            target: "configuringAdditionalFilters",
          },
          skipStep: {
            actions: notesReportModel.assign({
              collectionNoteIDRangeFilter: () => undefined,
            }),
            target: "configuringAdditionalFilters",
          },
          goBack: {
            target: "configuringSupplierFilter",
          },
        },
      },
      configuringAdditionalFilters: {
        id: "configuringAdditionalFilters",
        on: {
          provideAdditionalFilterSettings: {
            actions: notesReportModel.assign({
              includeCollectionNoteValues: (_, ev) => {
                return ev.data.includeCollectionNoteValues;
              },
              includeInvoicedCollectionNotes: (_, ev) => {
                return ev.data.includeInvoicedCollectionNotes;
              },
            }),
            target: "showingReportConfigSummary",
          },
          goBack: {
            target: "configuringNoteIDRange",
          },
        },
      },
      showingReportConfigSummary: {
        initial: "idle",
        states: {
          idle: {
            on: {
              generateReport: {
                target: "generatingReport",
              },
              goBack: {
                target: "#configuringAdditionalFilters",
              },
            },
          },
          generatingReport: {
            invoke: {
              id: "generatingReport",
              src: "generateReport",
              onError: {
                target: "#failure",
              },
            },
            on: {
              reportGenerationSuccess: {
                target: "#reportGenerated",
                actions: notesReportModel.assign({
                  reportURL: (_, ev) => ev.data.reportURL,
                }),
              },
              reportGenerationFailure: {
                target: "#failure",
                actions: notesReportModel.assign({
                  error: (_, ev) => ev.data.error,
                }),
              },
            },
          },
        },
      },
      reportGenerated: {
        id: "reportGenerated",
        entry: "openReportURL",
      },
      failure: {
        id: "failure",
        on: {
          restart: {
            target: "configuringDateRange",
            actions: notesReportModel.assign(() => {
              return notesReportModel.initialContext;
            }),
          },
        },
      },
    },
  },
  {
    actions: {
      openReportURL: async (ctx) => {
        if (!ctx.reportURL) {
          throw new Error("Response does not contain a url");
        }

        openReportURL(ctx.reportURL);
      },
    },
    services: {
      generateReport: (ctx, ev) => (cb) => {
        graphQLClient
          .query({
            query: GENERATE_NOTES_REPORT,
            variables: {
              input: {
                startDate: ctx.dateRange.startDate,
                endDate: ctx.dateRange.endDate,
                fromSupplierID: ctx.supplierFilter?.from.internalId,
                toSupplierID: ctx.supplierFilter?.to.internalId,
                fromCollectionNoteID: ctx.collectionNoteIDRangeFilter?.fromID,
                toCollectionNoteID: ctx.collectionNoteIDRangeFilter?.toID,
                includeCollectionNoteValues: ctx.includeCollectionNoteValues,
                includeInvoicedCollectionNotes: ctx.includeInvoicedCollectionNotes,
              },
            },
          })
          .then((res) => {
            const reportUrl = res.data.collectionNotesReport.url;

            cb(notesReportModel.events.reportGenerationSuccess(reportUrl));
          })
          .catch((e) => {
            cb(notesReportModel.events.reportGenerationFailure(e.message));
          });
      },
    },
  }
);

const GENERATE_NOTES_REPORT = gql`
  query GetCollectionNotesReportInfo($input: CollectionNotesReportInput!) {
    collectionNotesReport(input: $input) {
      url
    }
  }
`;
